/**
 * PWA-Update-Hook per CLAUDE.md §5.
 *
 * Drei Pfade beim `updatefound`-Event:
 *   1. Queue leer       → Reload sofort.
 *   2. Queue non-leer   → `meta.updatePendingSince` setzen, alle 60min prüfen.
 *   3. >28 Tage alt     → Force-Reload (Queue persistiert; neuer Build sendet
 *                         beim nächsten Online-Werden nach).
 *
 * `controllerchange` (eine andere Seite hat den neuen SW aktiviert) zeigt ein
 * 3-Sekunden-Hinweisoverlay und triggert dann `window.location.reload()`.
 *
 * Auto-Check alle 60min via `registration.update()`.
 */

import { useEffect, useState } from 'react';
import { countScanQueue, deleteMeta, getMeta, setMeta } from '@/lib/idb';

export const FORCE_THRESHOLD_DAYS = 28;
export const FORCE_THRESHOLD_MS = FORCE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
export const QUEUE_RECHECK_MS = 60 * 60 * 1000; // 60 Min
export const UPDATE_CHECK_MS = 60 * 60 * 1000; // 60 Min
export const CONTROLLER_CHANGE_OVERLAY_MS = 3_000;

export interface PWAUpdateState {
  /** True between `controllerchange` and the actual reload. */
  reloading: boolean;
  /**
   * True iff a new SW is waiting and the queue isn't empty yet (path 2).
   * The kiosk may surface a small banner so users know an update is pending.
   */
  updatePending: boolean;
}

interface InternalDeps {
  /** Override `Date.now()` — used by tests. */
  now?: () => number;
  /** Replaces `window.location.reload()` — used by tests. */
  reload?: () => void;
  /** Inject a registration so the hook can run without the real SW. */
  registration?: ServiceWorkerRegistration | null;
}

/**
 * NOTE: The hook uses `navigator.serviceWorker` directly so it stays useful
 * in Safari (where Background Sync is missing but the SW lifecycle works).
 */
export function usePWAUpdate(deps: InternalDeps = {}): PWAUpdateState {
  const [state, setState] = useState<PWAUpdateState>({
    reloading: false,
    updatePending: false,
  });

  useEffect(() => {
    const now = deps.now ?? Date.now;
    const reload =
      deps.reload ??
      ((): void => {
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      });

    let cancelled = false;
    let queueCheckTimer: ReturnType<typeof setInterval> | null = null;
    let updateCheckTimer: ReturnType<typeof setInterval> | null = null;
    let registration: ServiceWorkerRegistration | null = deps.registration ?? null;

    /**
     * Decision routine when an update has been detected. Picks one of the
     * three paths in §5.
     */
    async function decideReload(force: boolean = false): Promise<void> {
      if (cancelled) return;
      try {
        const pending = await countScanQueue();
        const at = now();
        if (pending === 0) {
          await deleteMeta('updatePendingSince');
          reload();
          return;
        }
        if (force) {
          // Force-reload: queue stays put, will be drained after reload.
          await deleteMeta('updatePendingSince');
          reload();
          return;
        }
        const stamp = (await getMeta('updatePendingSince')) ?? at;
        if (at - stamp >= FORCE_THRESHOLD_MS) {
          // 28-day threshold breached → force.
          await deleteMeta('updatePendingSince');
          reload();
          return;
        }
        if ((await getMeta('updatePendingSince')) === undefined) {
          await setMeta('updatePendingSince', at);
        }
        if (!cancelled) {
          setState((s) => ({ ...s, updatePending: true }));
        }
      } catch {
        // IndexedDB error — don't block the user, but don't auto-reload either.
      }
    }

    function onUpdateFound(): void {
      void decideReload(false);
    }

    function onControllerChange(): void {
      if (cancelled) return;
      setState((s) => ({ ...s, reloading: true }));
      window.setTimeout(() => {
        if (!cancelled) reload();
      }, CONTROLLER_CHANGE_OVERLAY_MS);
    }

    async function attach(): Promise<void> {
      if (!registration) {
        if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
          return;
        }
        try {
          registration = (await navigator.serviceWorker.getRegistration()) ?? null;
        } catch {
          registration = null;
        }
      }
      if (cancelled) return;

      if (registration) {
        registration.addEventListener('updatefound', onUpdateFound);
        // If a worker is already waiting (the page reloaded with the SW
        // already swapped), fall through into the same decision routine.
        if (registration.waiting) {
          void decideReload(false);
        }
      }

      if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
      }

      // Re-check the wait condition every 60min — covers the case where the
      // queue drained while we were sitting on `updatePending`.
      queueCheckTimer = setInterval(() => {
        void decideReload(false);
      }, QUEUE_RECHECK_MS);

      // Probe for a new SW every 60min.
      updateCheckTimer = setInterval(() => {
        if (registration) {
          void registration.update().catch(() => {});
        }
      }, UPDATE_CHECK_MS);
    }

    void attach();

    return () => {
      cancelled = true;
      if (queueCheckTimer) clearInterval(queueCheckTimer);
      if (updateCheckTimer) clearInterval(updateCheckTimer);
      if (registration) {
        registration.removeEventListener('updatefound', onUpdateFound);
      }
      if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

/**
 * Pure decision function — exported for tests so we don't need to mock the
 * full ServiceWorker lifecycle.
 */
export async function decideReloadAction(params: {
  pending: number;
  updatePendingSince: number | null;
  now: number;
}): Promise<'reload' | 'force-reload' | 'wait'> {
  if (params.pending === 0) return 'reload';
  const stamp = params.updatePendingSince ?? params.now;
  if (params.now - stamp >= FORCE_THRESHOLD_MS) return 'force-reload';
  return 'wait';
}
